// src/redux/usageSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  getUsageSnapshot,
  quoteUsage,
  consumeUsage,
} from "../services/usageService";

import { isApiLimitError } from "../services/apiService";

/* -------------------- helpers -------------------- */

const unwrap = (res) => {
  if (!res) return res;
  if (typeof res === "object" && "data" in res) return res.data;
  return res;
};

const getErrorStatus = (err) =>
  err?.status ?? err?.response?.status ?? err?.payload?.status ?? null;

const getErrorPayload = (err) =>
  err?.payload ?? err?.response?.data ?? err?.data ?? err ?? {};

const toStr = (value, fallback = "") => {
  const s = String(value ?? "").trim();
  return s || fallback;
};

const readErrorObject = (payload) => {
  const p = unwrap(payload) || {};

  if (p?.error && typeof p.error === "object") {
    return p.error;
  }

  return p;
};

const normalizeSnapshot = (res) => {
  const payload = unwrap(res) || {};

  return {
    tenant_id: payload.tenant_id || payload.tenantId || null,
    periodKey: payload.periodKey || payload.period_key || payload.period || null,
    counters: payload.counters || payload.usage || payload.meters || {},
    ok: payload.ok === false ? false : true,
  };
};

const normalizeLimitError = (
  rawPayload,
  {
    status,
    kind,
    fallbackMessage = "Your plan limit has been reached.",
  } = {}
) => {
  const p = unwrap(rawPayload) || {};
  const e = readErrorObject(p);

  const metadata =
    e?.metadata && typeof e.metadata === "object"
      ? e.metadata
      : p?.metadata && typeof p.metadata === "object"
        ? p.metadata
        : {};

  const resolvedStatus =
    status ?? p?.status ?? e?.status ?? metadata?.status ?? null;

  const resolvedCode = toStr(
    e?.code ||
      p?.code ||
      p?.errorCode ||
      metadata?.code ||
      "LIMIT_EXCEEDED"
  ).toUpperCase();

  const resolvedMessage = toStr(
    e?.message ||
      p?.message ||
      p?.detail ||
      metadata?.message ||
      fallbackMessage,
    fallbackMessage
  );

  const resolvedKind =
    kind ||
    p?.kind ||
    e?.kind ||
    metadata?.kind ||
    (resolvedStatus === 403 ? "feature_disabled" : "limit_exceeded");

  const upgradeCta =
    e?.upgrade_cta ||
    e?.upgradeCta ||
    p?.upgrade_cta ||
    p?.upgradeCta ||
    null;

  const upgradeUrl =
    e?.upgradeUrl ||
    e?.upgrade_url ||
    p?.upgradeUrl ||
    p?.upgrade_url ||
    upgradeCta?.url ||
    "/pricing";

  return {
    status: resolvedStatus,
    kind: resolvedKind,
    code: resolvedCode,
    message: resolvedMessage,
    meter:
      e?.meter ||
      e?.meterName ||
      e?.meter_name ||
      p?.meter ||
      p?.meterName ||
      p?.meter_name ||
      metadata?.meter ||
      "",
    limit: e?.limit ?? p?.limit ?? metadata?.limit,
    used: e?.used ?? p?.used ?? metadata?.used,
    remaining: e?.remaining ?? p?.remaining ?? metadata?.remaining,
    resetAt:
      e?.resetAt ||
      e?.reset_at ||
      p?.resetAt ||
      p?.reset_at ||
      metadata?.resetAt ||
      metadata?.reset_at ||
      null,
    upgradeRequired: Boolean(
      e?.upgradeRequired ??
        e?.upgrade_required ??
        p?.upgradeRequired ??
        p?.upgrade_required ??
        true
    ),
    upgradeUrl,
    upgrade_cta: upgradeCta,
    metadata: {
      ...metadata,
      upgradeUrl,
    },
    raw: p,
  };
};

const classify403 = (payload) => {
  const p = unwrap(payload) || {};
  const e = readErrorObject(p);

  const reason = toStr(e?.reason || p?.reason || e?.code || p?.code);
  const msg = toStr(e?.message || p?.message || p?.detail);

  const hay = `${reason} ${msg}`.toUpperCase();

  if (
    hay.includes("API_LIMIT") ||
    hay.includes("LIMIT_EXCEEDED") ||
    hay.includes("QUOTA") ||
    hay.includes("ALLOWANCE") ||
    hay.includes("USAGE") ||
    hay.includes("REACHED")
  ) {
    return "limit_exceeded";
  }

  return "feature_disabled";
};

const isBusinessGateStatus = (status) => status === 402 || status === 403;

const isLimitLikeError = (err) => {
  if (isApiLimitError(err)) return true;

  const status = getErrorStatus(err);
  if (isBusinessGateStatus(status)) return true;

  const payload = getErrorPayload(err);
  const e = readErrorObject(payload);

  const hay = `${e?.code || ""} ${e?.reason || ""} ${e?.message || ""} ${
    payload?.code || ""
  } ${payload?.message || ""}`.toUpperCase();

  return (
    hay.includes("LIMIT") ||
    hay.includes("QUOTA") ||
    hay.includes("UPGRADE") ||
    hay.includes("ALLOWANCE") ||
    hay.includes("ENTITLEMENT")
  );
};

const buildRejectedError = (err, fallback) => {
  const status = getErrorStatus(err);
  const payload = getErrorPayload(err);

  if (isLimitLikeError(err)) {
    const kind =
      err?.kind ||
      payload?.kind ||
      (status === 403 ? classify403(payload) : "limit_exceeded");

    return {
      ...normalizeLimitError(payload, {
        status,
        kind,
        fallbackMessage: err?.message || fallback,
      }),
      message: err?.message || normalizeLimitError(payload).message || fallback,
    };
  }

  return {
    message: err?.message || fallback,
    status,
    payload,
  };
};

/* -------------------- thunks -------------------- */

export const fetchUsageSnapshotAsync = createAsyncThunk(
  "usage/fetchSnapshot",
  async (periodKey, { rejectWithValue }) => {
    try {
      const res = await getUsageSnapshot(periodKey);
      return normalizeSnapshot(res);
    } catch (err) {
      return rejectWithValue(
        buildRejectedError(err, "Failed to load usage snapshot.")
      );
    }
  }
);

export const quoteUsageAsync = createAsyncThunk(
  "usage/quote",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await quoteUsage(payload);
      return unwrap(res) || {};
    } catch (err) {
      return rejectWithValue(buildRejectedError(err, "Failed to quote usage."));
    }
  }
);

export const consumeUsageAsync = createAsyncThunk(
  "usage/consume",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await consumeUsage(payload);
      return unwrap(res) || {};
    } catch (err) {
      return rejectWithValue(buildRejectedError(err, "Failed to consume usage."));
    }
  }
);

/* -------------------- slice -------------------- */

const initialState = {
  snapshotLoading: false,
  snapshotError: null,
  snapshot: {
    tenant_id: null,
    periodKey: null,
    counters: {},
    ok: true,
  },
  snapshotFetchedAt: 0,

  quoteLoading: false,
  quoteError: null,
  quoteResult: null,

  consumeLoading: false,
  consumeError: null,

  planGateError: null,
};

const usageSlice = createSlice({
  name: "usage",
  initialState,
  reducers: {
    clearUsageErrors(state) {
      state.snapshotError = null;
      state.quoteError = null;
      state.consumeError = null;
      state.planGateError = null;
    },
    clearPlanGateError(state) {
      state.planGateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsageSnapshotAsync.pending, (state) => {
        state.snapshotLoading = true;
        state.snapshotError = null;
      })
      .addCase(fetchUsageSnapshotAsync.fulfilled, (state, action) => {
        state.snapshotLoading = false;
        state.snapshot = action.payload || initialState.snapshot;
        state.snapshotFetchedAt = Date.now();
      })
      .addCase(fetchUsageSnapshotAsync.rejected, (state, action) => {
        state.snapshotLoading = false;
        state.snapshotError =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load usage.";
      })

      .addCase(quoteUsageAsync.pending, (state) => {
        state.quoteLoading = true;
        state.quoteError = null;
        state.quoteResult = null;
      })
      .addCase(quoteUsageAsync.fulfilled, (state, action) => {
        state.quoteLoading = false;
        state.quoteResult = action.payload || null;

        if (action.payload?.allowed === false) {
          state.planGateError = normalizeLimitError(action.payload, {
            status: action.payload?.status || 402,
            kind: "limit_exceeded",
            fallbackMessage:
              action.payload?.message || "Your plan limit has been reached.",
          });
        }
      })
      .addCase(quoteUsageAsync.rejected, (state, action) => {
        state.quoteLoading = false;

        const payload = action.payload || {};
        const kind = payload?.kind;

        if (kind === "limit_exceeded" || kind === "feature_disabled") {
          state.planGateError = payload;
          state.quoteError = null;
          return;
        }

        state.quoteError =
          payload?.message ||
          action.error?.message ||
          "Failed to quote usage.";
      })

      .addCase(consumeUsageAsync.pending, (state) => {
        state.consumeLoading = true;
        state.consumeError = null;
        state.planGateError = null;
      })
      .addCase(consumeUsageAsync.fulfilled, (state) => {
        state.consumeLoading = false;
      })
      .addCase(consumeUsageAsync.rejected, (state, action) => {
        state.consumeLoading = false;

        const payload = action.payload || {};
        const kind = payload?.kind;

        if (kind === "limit_exceeded" || kind === "feature_disabled") {
          state.planGateError = payload;
          state.consumeError = null;
          return;
        }

        state.consumeError =
          payload?.message ||
          action.error?.message ||
          "Failed to consume usage.";
      });
  },
});

export const { clearUsageErrors, clearPlanGateError } = usageSlice.actions;

/* -------------------- selectors -------------------- */

export const selectUsageSnapshot = (state) =>
  state.usage?.snapshot || initialState.snapshot;

export const selectUsageSnapshotLoading = (state) =>
  state.usage?.snapshotLoading || false;

export const selectUsageSnapshotError = (state) =>
  state.usage?.snapshotError || null;

export const selectApiUsageFromSnapshot = (state) => {
  const snap = selectUsageSnapshot(state);
  const counters = snap?.counters || {};

  const meterObj =
    counters.api_requests ||
    counters.apiRequests ||
    counters.api ||
    counters.requests ||
    null;

  const fallbackLooksLikeMeter =
    counters &&
    typeof counters === "object" &&
    ("used" in counters ||
      "limit" in counters ||
      "max" in counters ||
      "monthly_limit" in counters ||
      "monthlyLimit" in counters ||
      "current_usage" in counters ||
      "currentUsage" in counters);

  const meter = meterObj || (fallbackLooksLikeMeter ? counters : {});

  const usedRaw =
    meter.current_usage ??
    meter.currentUsage ??
    meter.used ??
    meter.usage ??
    counters.api_requests_used ??
    counters.apiRequestsUsed ??
    counters.used ??
    0;

  const limitRaw =
    meter.monthly_limit ??
    meter.monthlyLimit ??
    meter.limit ??
    meter.max ??
    counters.api_requests_limit ??
    counters.apiRequestsLimit ??
    counters.limit ??
    0;

  const used = Number(usedRaw || 0);
  const limit = Number(limitRaw || 0);

  return {
    current_usage: Number.isFinite(used) ? used : 0,
    monthly_limit: Number.isFinite(limit) ? limit : 0,
    periodKey: snap?.periodKey || null,
  };
};

export const selectQuoteLoading = (state) =>
  state.usage?.quoteLoading || false;

export const selectQuoteResult = (state) =>
  state.usage?.quoteResult || null;

export const selectConsumeLoading = (state) =>
  state.usage?.consumeLoading || false;

export const selectConsumeError = (state) =>
  state.usage?.consumeError || null;

export const selectPlanGateError = (state) =>
  state.usage?.planGateError || null;

export default usageSlice.reducer;